import type { OwaSanitizerConfig } from "../owaSanitizerConfig";

/**
 * Field separator. A control character is used because it cannot appear in an
 * HTML element or attribute name, so the resulting key is unambiguous.
 */
const SEPARATOR = "\u0001";

/**
 * How each field contributes to the key.
 *
 * - `list`   — a string list; contributes a length prefix plus its entries.
 * - `flag`   — a boolean that is off unless set.
 * - `flagOn` — a boolean that is on unless explicitly set to false, so an omitted
 *              field and an explicit `true` share a key (matches the defaults
 *              applied by SanitizerConfigAdapter).
 */
type FieldKind = "list" | "flag" | "flagOn";

/**
 * Restricts each field to the kinds that are valid for its declared type, so a
 * string-list field cannot be classified as a boolean flag (which would collapse
 * every non-empty list to the same key) and vice versa.
 */
type KindFor<T> = NonNullable<T> extends string[] ? "list" : "flag" | "flagOn";

/**
 * Every field of OwaSanitizerConfig, classified.
 *
 * The mapped type makes this **exhaustive by construction**: adding a field to
 * OwaSanitizerConfig fails to compile until it is classified here, and because the
 * key is generated from this map, classifying it is all that is needed to include
 * it. `KindFor` additionally rejects a classification that does not match the
 * field's type.
 *
 * That guard matters because the failure it prevents is silent. A field missing
 * from the key does not throw — it makes two configs that differ only by that
 * field collide in the sanitizer cache, so a caller can receive HTML sanitized
 * under someone else's config.
 *
 * Declaration order defines the key layout. Reordering is safe (the caches are
 * in-memory and rebuilt per page load) but pointless, and it invalidates every
 * live entry, so prefer appending.
 */
const CONFIG_FIELDS: {
    [K in keyof OwaSanitizerConfig]-?: KindFor<OwaSanitizerConfig[K]>;
} = {
    additionalTags: "list",
    additionalAttributes: "list",
    forbiddenTags: "list",
    forbiddenAttributes: "list",
    additionalUriSafeAttributes: "list",
    unwrapTags: "list",
    allowUnknownProtocols: "flag",
    allowDataAttributes: "flagOn",
    allowComments: "flag",
    wholeDocument: "flag",
    forceBody: "flag",
    svgProfile: "flag",
};

function appendList(parts: string[], list: readonly string[] | undefined): void {
    if (!list?.length) {
        parts.push("0");
        return;
    }
    
    // The length prefix keeps the key injective: ['a,b'] and ['a', 'b'] must not
    // produce the same key.
    parts.push(String(list.length));
    for (const entry of list) {
        parts.push(entry);
    }
}

/**
 * Builds a canonical, injective cache key for an OwaSanitizerConfig.
 *
 * Used both to memoize translated sanitizer configs and to key the sanitized-HTML
 * caches, so the key is computed once per call rather than serialized twice.
 *
 * Preferred over JSON.stringify because it reads a fixed field order, making the
 * key independent of the property order used at the call site — two structurally
 * equal configs written differently still share a memo entry.
 */
export function getSanitizerConfigKey(config: OwaSanitizerConfig): string {
    const parts: string[] = [];
    let flags = "";
    
    for (const field of Object.keys(CONFIG_FIELDS) as (keyof OwaSanitizerConfig)[]) {
        const kind: FieldKind = CONFIG_FIELDS[field];
        const value = config[field];
        
        if (kind === "list") {
            appendList(parts, value as string[] | undefined);
        }
        else {
            flags += (kind === "flagOn" ? value !== false : !!value) ? "1" : "0";
        }
    }
    
    parts.push(flags);
    
    return parts.join(SEPARATOR);
}
